import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Collapse } from 'antd';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import type { Moment } from 'moment';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteIcon from 'src/shared/assets/icons/delete.svg';
import DotsIcon from 'src/shared/assets/icons/dots.svg';
import { Button } from 'src/shared/components/button';
import { FormDatePicker, FormNumberInput, FormSelect } from 'src/shared/components/form-components';
import { FormTooltip } from 'src/shared/components/form-components/form-tooltip';
import { hasValue } from 'src/shared/utils/common';

import { GetDirectoryValuesButton } from '../../components/get-directory-values-button';
import type { RigOperationSection } from '../../entities/rig-operations/rig-operation-section.entity';
import type { RigOperationStage } from '../../entities/rig-operations/rig-operation-stage.entity';
import type { RigOperation } from '../../entities/rig-operations/rig-operation.entity';
import type { RigOperationsViewModel } from '../../entities/rig-operations/rig-operations-view-model';
import { SectionDropdown } from '../section-dropdown/section-dropdown';
import { SectionItem } from '../section-item';

import styles from './stage-item.module.scss';

type Props = {
  stage: RigOperationStage;
  rigOperation: RigOperation;
  rigOperationsViewModel: RigOperationsViewModel;
  onValueChange(fieldId: string, value: unknown, section?: RigOperationSection): void;
  onAddSection(sectionTypeId: number): void;
  onDeleteStage(): void;
  onDeleteSection(section: RigOperationSection): void;
  isFieldBlocked(fieldId: string, section?: RigOperationSection): boolean;
  isFieldVisible(fieldId: string, section?: RigOperationSection): boolean;
  getDefaultTime(fieldId: 'startDate' | 'endDate', section?: RigOperationSection): Moment;
  getDisabledTime(fieldId: 'startDate' | 'endDate', section?: RigOperationSection): Moment | null;
  setDirectoryValue(fieldId: string, section?: RigOperationSection): void;
  moveSection(event: DragEndEvent): void;
};

export const StageItem = observer<Props>(function StageItem({
  stage,
  rigOperation,
  rigOperationsViewModel,
  onValueChange,
  onAddSection,
  onDeleteStage,
  onDeleteSection,
  isFieldBlocked,
  isFieldVisible,
  getDefaultTime,
  getDisabledTime,
  setDirectoryValue,
  moveSection,
}) {
  const { t } = useTranslation();
  const { isDragging, attributes, listeners, transform, transition, setNodeRef, setActivatorNodeRef } = useSortable({
    id: stage.entityId,
  });

  const {
    stageTypeOptions,
    sectionTypeOptions,
    isFormDisabled,
    hasSections: checkHasSections,
    validationService,
    restoreToDirectoryValueLoadingState,
    isDrillingStage,
    getValidSelectValue,
  } = rigOperationsViewModel;

  const hasSections = checkHasSections(stage.wellLifeCycleStageId);

  const startDate = useMemo(() => {
    if (!hasValue(stage.startDate)) {
      return null;
    }

    return moment(stage.startDate * 1000);
  }, [stage.startDate]);

  const endDate = useMemo(() => {
    if (!hasValue(stage.endDate)) {
      return null;
    }

    return moment(stage.endDate * 1000);
  }, [stage.endDate]);

  const dragStyles = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const isCommercialSpeedFieldBlocked = isFieldBlocked('commercialSpeed');
  const isDurationFieldBlocked = isFieldBlocked('duration');
  const defaultStartDate = getDefaultTime('startDate');
  const defaultEndDate = getDefaultTime('endDate');
  const disabledStartDate = getDisabledTime('startDate');
  const disabledEndDate = getDisabledTime('endDate');

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={dragStyles}
      className={clsx(styles.rowWrapper, isDragging && styles.rowWrapper__active)}
    >
      <Collapse expandIcon={() => null} activeKey={isDragging ? undefined : stage.entityId}>
        <Collapse.Panel
          key={stage.entityId}
          header={
            <div className={styles.rowMainContentWrapper}>
              {!isFormDisabled && (
                <>
                  <div ref={setActivatorNodeRef} className={styles.grabIcon__wrapper} {...listeners}>
                    <DotsIcon className={styles.grabIcon} />
                  </div>
                  <Button
                    variant="default"
                    className={styles.deleteButton}
                    icon={<DeleteIcon width={18} height={18} />}
                    onClick={onDeleteStage}
                  />
                </>
              )}

              <div className={styles.fieldsWrapper}>
                <FormTooltip text={stage.errors.wellLifeCycleStageId}>
                  <FormSelect
                    value={getValidSelectValue('wellLifeCycleStageId', stage.wellLifeCycleStageId)}
                    allowClear={false}
                    isDisabled={isFieldBlocked('wellLifeCycleStageId')}
                    isError={!!stage.errors.wellLifeCycleStageId}
                    options={stageTypeOptions.actual}
                    placeholder="Выберите значение"
                    onChange={(value) => onValueChange('wellLifeCycleStageId', value)}
                    onBlur={() => validationService.validateStageType(rigOperation, stage)}
                    onFocus={() => stage.removeError('wellLifeCycleStageId')}
                  />
                </FormTooltip>

                <div className={styles.dateIntervalWrapper}>
                  <div>
                    <p className={styles.fieldLabel}>Дата начала</p>
                    <FormTooltip text={stage.errors.startDate}>
                      <FormDatePicker
                        withTime
                        value={startDate}
                        defaultTime={defaultStartDate}
                        defaultDate={defaultStartDate}
                        disabledDate={(date) => date.diff(disabledStartDate) < 0}
                        disabled={isFieldBlocked('startDate')}
                        isError={!!stage.errors.startDate}
                        placeholder="Выберите дату"
                        onChange={(value) => onValueChange('startDate', value)}
                        onBlur={() => validationService.validateStartDate(rigOperation, stage)}
                        onFocus={() => stage.removeError('startDate')}
                      />
                    </FormTooltip>
                  </div>
                  <p className={styles.dash}>—</p>
                  <div>
                    <p className={styles.fieldLabel}>Дата окончания</p>
                    <FormTooltip text={stage.errors.endDate}>
                      <FormDatePicker
                        withTime
                        value={endDate}
                        defaultTime={defaultEndDate}
                        defaultDate={defaultEndDate}
                        disabledDate={(date) => date.diff(disabledEndDate) < 0}
                        disabled={isFieldBlocked('endDate')}
                        isError={!!stage.errors.endDate}
                        placeholder="Выберите дату"
                        onChange={(value) => onValueChange('endDate', value)}
                        onBlur={() => validationService.validateEndDate(rigOperation, stage)}
                        onFocus={() => stage.removeError('endDate')}
                      />
                    </FormTooltip>
                  </div>
                </div>

                <div>
                  <p className={styles.fieldLabel}>Срок</p>
                  <FormTooltip text={stage.errors.duration}>
                    <FormNumberInput
                      value={stage.duration}
                      isDisabled={isDurationFieldBlocked}
                      isError={!!stage.errors.duration}
                      allowClear={false}
                      numberOfDecimalPlaces={2}
                      suffixComponent={
                        isDrillingStage(stage.wellLifeCycleStageId) ? undefined : (
                          <GetDirectoryValuesButton
                            isDisabled={isDurationFieldBlocked}
                            isLoading={restoreToDirectoryValueLoadingState['duration']}
                            onClick={() => setDirectoryValue('duration')}
                          />
                        )
                      }
                      onChange={(value) => onValueChange('duration', value)}
                      onBlur={() => validationService.validateDurationField(rigOperation, stage)}
                      onFocus={() => stage.removeError('duration')}
                      placeholder="Введите значение"
                      unit="Суток"
                    />
                  </FormTooltip>
                </div>

                {isFieldVisible('distance') && (
                  <div>
                    <p className={styles.fieldLabel}>Расстояние</p>
                    <FormTooltip text={stage.errors.distance}>
                      <FormNumberInput
                        value={stage.distance}
                        isDisabled={isFieldBlocked('distance')}
                        isError={!!stage.errors.distance}
                        onChange={(value) => onValueChange('distance', value)}
                        onFocus={() => stage.removeError('distance')}
                        placeholder="Введите значение"
                        unit="км"
                      />
                    </FormTooltip>
                  </div>
                )}

                {isFieldVisible('passingValue') && (
                  <div>
                    <p className={styles.fieldLabel}>Проходка</p>
                    <FormTooltip text={stage.errors.passingValue}>
                      <FormNumberInput
                        value={stage.passingValue}
                        isDisabled={isFieldBlocked('passingValue')}
                        isError={!!stage.errors.passingValue}
                        onChange={(value) => onValueChange('passingValue', value)}
                        onBlur={() => validationService.validatePassingValueField(rigOperation, stage)}
                        onFocus={() => stage.removeError('passingValue')}
                        placeholder="Введите значение"
                        unit="м"
                      />
                    </FormTooltip>
                  </div>
                )}
                {isFieldVisible('commercialSpeed') && (
                  <div>
                    <p className={styles.fieldLabel}>Комм скорость</p>
                    <FormTooltip text={stage.errors.commercialSpeed}>
                      <FormNumberInput
                        value={stage.commercialSpeed}
                        numberOfDecimalPlaces={0}
                        suffixComponent={
                          <GetDirectoryValuesButton
                            isDisabled={isCommercialSpeedFieldBlocked}
                            isLoading={restoreToDirectoryValueLoadingState['commercialSpeed']}
                            onClick={() => setDirectoryValue('commercialSpeed')}
                          />
                        }
                        isDisabled={isCommercialSpeedFieldBlocked}
                        isError={!!stage.errors.commercialSpeed}
                        onChange={(value) => onValueChange('commercialSpeed', value)}
                        onBlur={() => validationService.validateCommercialSpeed(rigOperation, stage)}
                        onFocus={() => stage.removeError('commercialSpeed')}
                        placeholder="Введите значение"
                      />
                    </FormTooltip>
                  </div>
                )}
              </div>
            </div>
          }
        >
          {hasSections && (
            <div className={styles.sectionsWrapper}>
              <DndContext
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={(event) => moveSection(event)}
              >
                <SortableContext items={stage.sections.map((s) => s.entityId)} strategy={verticalListSortingStrategy}>
                  {stage.sections.map((section) => (
                    <SectionItem
                      key={section.entityId}
                      section={section}
                      // Временные пропы для избежания прокидывания большого кол-ва обработчиков валидации
                      stage={stage}
                      rigOperation={rigOperation}
                      //
                      rigOperationsViewModel={rigOperationsViewModel}
                      onValueChange={(fieldId, value, section) => onValueChange(fieldId, value, section)}
                      onDeleteSection={() => onDeleteSection(section)}
                      isFieldBlocked={(fieldId): boolean => isFieldBlocked(fieldId, section)}
                      getDefaultTime={(fieldId) => getDefaultTime(fieldId, section)}
                      getDisabledTime={(fieldId) => getDisabledTime(fieldId, section)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {hasSections && (
            <SectionDropdown
              options={sectionTypeOptions.actual}
              isDisabled={isFormDisabled}
              onNewSection={(value) => onAddSection(value)}
            />
          )}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
});
